/**
 * Koppelvoorstellen bij een project (wiki 26 sep, Project-overzicht):
 *  - OneNote-pagina's voorstellen op projectnummer, klantnaam en projectnaam
 *    (zelfde matching als /notes/onenote-import: nummer eerst, dan klantnaam);
 *  - de 'alles gekoppeld?'-tip van de opstartwizard: welke bronnen hangen er
 *    nog niet aan het project.
 * Pure functies — de schermen doen het ophalen en koppelen.
 */

const PROJECTNR = /(?<!\d)([5-9]\d{3})(?!\d)/g;
const norm = (s) => ` ${String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()} `;
const GENERIEK = new Set(['gemeente', 'stichting', 'holding', 'groep', 'group', 'nederland', 'bv', 'nv', 'b v', 'n v']);

/** Waarom hoort deze OneNote-pagina (misschien) bij het project? '' = niet. */
export function oneNoteReden(pagina, { nummer, klant, naam } = {}) {
  const titel = String(pagina?.title || '');
  if (/^\s*template\b/i.test(titel)) return ''; // sjabloonpagina's nooit voorstellen
  const nr = String(nummer || '').trim();
  if (nr && [...titel.matchAll(PROJECTNR)].some((m) => m[1] === nr)) return `projectnummer ${nr}`;
  const t = norm(titel);
  const k = norm(klant).trim();
  if (k.length >= 3 && !GENERIEK.has(k) && t.includes(` ${k} `)) return 'klantnaam';
  // Eén kenmerkend woord uit de klantnaam ('Alliander' uit 'Alliander NV').
  const kWoord = k.split(' ').find((w) => w.length >= 5 && !GENERIEK.has(w));
  if (kWoord && t.includes(` ${kWoord} `)) return 'klantnaam';
  const n = norm(naam).trim();
  if (n.length >= 5 && t.includes(` ${n} `)) return 'projectnaam';
  return '';
}

const BRON_RE = /onenote:([A-Za-z0-9!._-]+)/g;
const escapeRe = (x) => String(x).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * OneNote-pagina's die al als notitie bij dit project staan (bronregel onenote:<id>).
 * → Map pageId → { noteId, note }.
 */
export function gekoppeldeOneNoteIds(notes = []) {
  const ids = new Map();
  (notes || []).forEach((n) => {
    const tekst = String(n?.fields?.Notes || n?.notes || '');
    [...tekst.matchAll(BRON_RE)].forEach((m) => { if (!ids.has(m[1])) ids.set(m[1], { noteId: n.id, note: n }); });
  });
  return ids;
}

/** De notitie zoals de koppeling hem aanmaakt — ook de maatstaf voor 'onbewerkt'. */
export function oneNoteNotitie(pagina, tekst) {
  return {
    titel: String(pagina?.title || 'OneNote').slice(0, 100),
    inhoud: `${String(tekst || '').trim() || '(lege pagina)'}\n\n—\nBron: OneNote${pagina?.webUrl ? ` · ${pagina.webUrl}` : ''}\nonenote:${pagina?.id}`,
  };
}

/** Is de notitie sinds de import niet aangepast (titel én tekst gelijk aan wat de koppeling maakte)? */
export function isOnbewerkt(note, verwacht) {
  const f = note?.fields || {};
  const norm = (x) => String(x || '').replace(/\r\n/g, '\n').trim();
  return norm(f.Notes) === norm(verwacht?.inhoud) && norm(f.Onderwerp) === norm(verwacht?.titel);
}

/** Alleen de koppeling weghalen: de regel onenote:<id> eruit, de rest van de notitie blijft. */
export function zonderOneNoteBron(tekst, pageId) {
  return String(tekst || '')
    .replace(new RegExp(`\\n?onenote:${escapeRe(pageId)}(?![A-Za-z0-9!._-])`, 'g'), '')
    .replace(/\s+$/, '');
}

/** Gekoppelde pagina als voorstel-rij, ook als de zoektocht hem niet (meer) vindt. */
function uitNotitie(pageId, { note }) {
  const f = note?.fields || {};
  const url = (String(f.Notes || '').match(/Bron: OneNote · (\S+)/) || [])[1] || null;
  return { id: pageId, title: f.Onderwerp || 'OneNote-pagina', modified: f.Notitiedatum || null, webUrl: url };
}

/**
 * Voorstellen: ontdubbeld, met reden, al gekoppelde bovenaan, dan de nieuwste.
 * Bestaande koppelingen staan er altijd in — ook buiten de zoekresultaten —
 * zodat je ze hier kunt terugzien en ontkoppelen.
 */
export function oneNoteVoorstellen(paginas = [], ctx = {}, gekoppeld = new Map()) {
  const perId = new Map((paginas || []).filter((p) => p?.id).map((p) => [p.id, p]));
  gekoppeld.forEach((info, pageId) => { if (!perId.has(pageId)) perId.set(pageId, uitNotitie(pageId, info)); });
  return [...perId.values()]
    .map((p) => ({ ...p, reden: gekoppeld.has(p.id) ? 'gekoppeld' : oneNoteReden(p, ctx), gekoppeld: gekoppeld.has(p.id) }))
    .filter((p) => p.reden)
    .sort((a, b) => (Number(b.gekoppeld) - Number(a.gekoppeld))
      || String(b.modified || '').localeCompare(String(a.modified || '')));
}

/** Staat er al een mail als projectnotitie (mail-koppeling / correspondentie → notitie)? */
export function heeftMailNotitie(notes = []) {
  return (notes || []).some((n) => /^\s*(\*\*Mail van:\*\*|— Uit e-mail(gesprek)?:)/m
    .test(String(n?.fields?.Notes || n?.notes || '')));
}

/**
 * 'Alles gekoppeld?' — welke bronnen ontbreken nog (voor de rustige tip bovenin
 * de opstartwizard). `check` = /opstart-check; onenote = {gekoppeld}|null (null = zoekt nog);
 * mail = true/false (opgeslagen koppeling of mailnotitie) of null (onbekend).
 * Alles telt op wat aan het project is OPGESLAGEN, niet op persoonlijke zoektreffers.
 */
export function ontbrekendeBronnen(check, { onenote = null, mail = null } = {}) {
  if (!check || check.error) return [];
  const uit = [];
  if (!check.briefing?.gekoppeld) uit.push({ key: 'briefing', label: 'briefing' });
  if (mail === false) uit.push({ key: 'mail', label: 'mail' });
  if (!(check.meetings?.gekoppeld > 0)) uit.push({ key: 'meetings', label: 'meetings' });
  if (onenote && !onenote.error && !(onenote.gekoppeld > 0)) uit.push({ key: 'onenote', label: 'OneNote' });
  return uit;
}
